import React, {
  Suspense,
  LazyExoticComponent,
  ReactElement,
} from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import Routes from 'Routes';
import styled from 'styled-components';
import XtraBreadcrumbs from 'components/XtraBreadcrumbs';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';

const HallPageHeader: LazyExoticComponent<() => ReactElement> | (() => ReactElement) = () => <div />;

const StyledXtraBreadcrumbs = styled(XtraBreadcrumbs)<{ hasFilter: boolean }>`
  padding-top: 16px;

  @media (min-width: 960px) {
    display: ${({ hasFilter }) => (hasFilter ? 'none' : 'block')};
  }
`;

const FilteringResultsText = styled.div<{ color: string }>`
  flex-basis: 100%;
  font-weight: bold;
  font-size: 18px;
  line-height: 18px;
  letter-spacing: -0.408px;
  text-transform: capitalize;
  color: ${({ color }) => color};
  padding: 16px;
  display: none;

  @media (min-width: 960px) {
    display: block;
  }
`;

const HeaderComponentWrapper = styled.div`
  width: 100%;
  position: ${({ headerOffset }) => (headerOffset ? 'absolute' : 'initial')};
  border-radius: 0 !important;
  top: -${({ headerOffset }: { headerOffset: number }) => headerOffset}px;
  left: 0;
`;

const HeaderComponent = ({
  isFiltered,
  filterLength,
  headerOffset,
  hasFilter,
  className = '',
}: {
  isFiltered: boolean;
  filterLength: number;
  headerOffset: number;
  hasFilter: boolean;
  className?: string;
}): React.ReactElement => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const { pageNames: { lobby, hall }, themeColor } = useSelector((s: any) => s.eventConfigs);
  const lobbyName = lobby?.[locale] || t('page.lobby', 'Lobby');
  const hallName = hall?.[locale] || t('page.hall', 'Exhibition Hall');

  return (
    <HeaderComponentWrapper className={className} headerOffset={headerOffset}>
      <Suspense fallback={null}>
        <StyledXtraBreadcrumbs
          hasFilter={hasFilter}
          paths={
            [
              { name: lobbyName, to: Routes.lobby },
              { name: hallName },
            ]
          }
        />
        <HallPageHeader />
        {(isFiltered && hasFilter) && (
          <FilteringResultsText color={themeColor || DEFAULT_THEME_COLOR}>
            {t('filter.total_filtered_results', 'Filtering results: %{total}', { total: filterLength })}
          </FilteringResultsText>
        )}
      </Suspense>
    </HeaderComponentWrapper>
  );
};

export default HeaderComponent;
